/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, X } from "lucide-react";
import type { SafeCircleNotification } from "@/types/safecircle";
import { useSafeCircleNotifications } from "@/hooks/useSafeCircleNotifications";
import { shouldPersistToastRead } from "@/utils/safecircle/toastReadPersist";

const TOAST_MS = 6000;

export default function NotificationToast() {
  const { inbox, latestFcm, markRead } = useSafeCircleNotifications();
  const [queue, setQueue] = useState<SafeCircleNotification[]>([]);
  const [seenIds] = useState(() => new Set<string>());

  // Surface newly-arrived inbox items
  useEffect(() => {
    const unread = inbox.filter((n) => !n.readAt && !seenIds.has(n.id));
    if (unread.length > 0) {
      unread.forEach((n) => seenIds.add(n.id));
      setQueue((q) => [...q, ...unread]);
    }
  }, [inbox, seenIds]);

  // Surface foreground FCM events
  useEffect(() => {
    if (latestFcm && !seenIds.has(latestFcm.id)) {
      seenIds.add(latestFcm.id);
      setQueue((q) => [...q, latestFcm]);
    }
  }, [latestFcm, seenIds]);

  // Persist readAt for a real (RTDB-backed) notification. FCM-only toasts have
  // a synthetic `fcm_*` id with no RTDB node, so skip those.
  const persistRead = (n: SafeCircleNotification) => {
    if (shouldPersistToastRead(n.id)) {
      markRead(n.id);
    }
  };

  // Auto-dismiss. CRUCIAL: mark the toasted item read in RTDB as it times out —
  // NOT just slice it off the local queue. Without this, an unread inbox item
  // was shown once, dropped from the in-memory queue, but left readAt-less in
  // RTDB; on the NEXT app session the (fresh, empty) seenIds set no longer
  // suppressed it, so onValue replayed the same "accepted" toast on every
  // login. Persisting readAt here makes the toast one-shot across sessions.
  useEffect(() => {
    if (queue.length === 0) return;
    const id = setTimeout(() => {
      setQueue((q) => {
        const [shown, ...rest] = q;
        if (shown) persistRead(shown);
        return rest;
      });
    }, TOAST_MS);
    return () => clearTimeout(id);
    // persistRead is stable enough (markRead is a useCallback); queue drives it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  const current = queue[0];
  if (!current) return null;

  const dismiss = () => {
    persistRead(current);
    setQueue((q) => q.slice(1));
  };

  const palette =
    current.type === "SOS_TRIGGERED"
      ? "border-destructive/40 bg-destructive/15 text-destructive"
      : current.type === "WALK_ME_HOME_OVERDUE"
        ? "border-amber-500/40 bg-amber-500/15 text-amber-500"
        : "border-primary/40 bg-primary/15 text-primary";

  return (
    <AnimatePresence>
      <motion.div
        key={current.id}
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        className={`fixed top-4 left-3 right-3 z-[90] glass-card p-3 border shadow-xl ${palette} max-w-md md:left-1/2 md:-translate-x-1/2 md:right-auto md:w-full`}
      >
        <div className="flex items-start gap-2">
          <Bell size={16} className="shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{current.title}</p>
            <p className="text-xs text-muted-foreground">{current.body}</p>
          </div>
          <button
            onClick={dismiss}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label="dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
